import { ChevronRight } from "lucide-react";
import { useId, useState, type ReactNode } from "react";

import type { LibraryAsset } from "@/api/library";
import { formatDate } from "@/lib/format";
import { Badge } from "@/ui/Badge";
import { AssetStateBadges } from "@/ui/library/AssetStateBadges";

/**
 * The metadata strip and Details disclosure every canonical library detail
 * carries (#492).
 *
 * One component for three surfaces — the main-concern detail, the BG-set file
 * editor, the BG-set domain editor — because the canonical reference renders the
 * same strip on all three and a second copy is how "Used by 3 projects" comes to
 * disagree with itself between two screens.
 */

const SEEDED_SOURCE = "Base library (seeded)";
const AUTHORED_SOURCE = "Authored in this editor";

/**
 * The canonical protection explanation, in the SERVICE's terms where it has any.
 *
 * The canonical reference states this as a tooltip on a "Base library" badge.
 * This renders it as text on screen instead, deliberately: a refusal a clinician
 * can only discover by hovering is not an explanation, and #33's whole point was
 * that read-only content must say why on the surface.
 */
export function protectionExplanation(asset: LibraryAsset): string | null {
  if (asset.capabilities.seeded) {
    return "Part of the base clinical library. The original stays as it is — make an editable copy to create your own version.";
  }
  if (asset.capabilities.archived) {
    return "This asset is archived. It has to be restored before a new version can be published; restoring is an administrator action.";
  }
  return null;
}

export function NamedAssetMeta({
  asset,
  version,
  contentHash,
  identity,
  details,
}: {
  asset: LibraryAsset;
  /** The version on screen, which during a stale recovery is not the head. */
  version: string;
  contentHash?: string | undefined;
  /** The canonical `section/name` identity shown under Details. */
  identity: string;
  /**
   * Family-specific evidence, appended to the Details list.
   *
   * A slot rather than a widened prop list because what belongs here is the
   * family's own: a Summary prompt's target and the exact pack file it writes
   * (#863) mean nothing on a main concern, and the four rows above are what
   * every canonical detail shares. Rendered INSIDE the same `dl`, so a
   * clinician reads one block of evidence rather than two lists with different
   * disclosure states.
   */
  details?: ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const detailsId = useId();
  const adoption = asset.capabilities.adoptionCount;
  const explanation = protectionExplanation(asset);

  return (
    <div className="space-y-1.5">
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground">
        <Badge tone="outline" className="font-mono">
          {version}
        </Badge>
        <Badge tone="outline">
          {asset.capabilities.seeded ? SEEDED_SOURCE : AUTHORED_SOURCE}
        </Badge>
        <AssetStateBadges capabilities={asset.capabilities} />
        <span aria-hidden="true">·</span>
        <span>{asset.createdBy}</span>
        <span aria-hidden="true">·</span>
        <span>Updated {formatDate(asset.updatedAt)}</span>
        <span aria-hidden="true">·</span>
        <span>
          Used by {adoption} {adoption === 1 ? "project" : "projects"}
        </span>
      </div>

      {explanation ? (
        <p className="text-xs text-muted-foreground">{explanation}</p>
      ) : null}

      <button
        type="button"
        aria-expanded={open}
        aria-controls={detailsId}
        className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
        onClick={() => setOpen((value) => !value)}
      >
        <ChevronRight
          aria-hidden="true"
          className={`h-3 w-3 transition-transform ${open ? "rotate-90" : ""}`}
        />
        Details
      </button>

      {open ? (
        <dl
          id={detailsId}
          className="grid gap-0.5 font-mono text-[11px] text-muted-foreground"
        >
          <div className="break-all">
            identity: {identity}
            {contentHash ? `@${contentHash}` : ""}
          </div>
          <div>version: {version}</div>
          <div>
            source: {asset.capabilities.seeded ? SEEDED_SOURCE : AUTHORED_SOURCE}
          </div>
          <div>adoption_count: {adoption}</div>
          {details}
        </dl>
      ) : null}
    </div>
  );
}
